import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { scoreColor } from "@/lib/scoreScale";
import { cn } from "@/lib/utils";
import { Capture, DESKTOP, MOBILE, Num, type Marker } from "./StorePage";
import { MONO } from "./theme";

/* ------------------------------------------------------------------ */
/* Small parts                                                         */
/* ------------------------------------------------------------------ */

export type Level = "High" | "Medium" | "Low";

/**
 * Severity as an outlined pill with a three-step bar glyph, so the level reads
 * from shape and label as well as colour. No red or amber: the brand green
 * marks the top level, everything else stays neutral.
 */
export function Sev({ level, label }: { level: Level; label?: string }) {
  const steps = level === "High" ? 3 : level === "Medium" ? 2 : 1;
  const tone =
    level === "High"
      ? "border-[hsl(var(--ec-mint)/0.5)] bg-primary/[0.08] text-[hsl(var(--ec-mint))]"
      : level === "Medium"
        ? "border-white/[0.22] text-foreground/90"
        : "border-foreground/[0.12] text-[hsl(var(--ec-muted))]";
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border py-[3px] pl-[7px] pr-[9px] text-[11.5px] font-medium", tone)}>
      <span aria-hidden className="inline-flex h-[11px] items-end gap-[2px]">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              "w-[3px] rounded-[1px]",
              i < steps ? (level === "High" ? "bg-[hsl(var(--ec-mint))]" : "bg-foreground/80") : "bg-white/[0.16]",
            )}
            style={{ height: 5 + i * 3 }}
          />
        ))}
      </span>
      {label ?? level}
    </span>
  );
}

export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded-full border border-border px-[9px] py-[3px] text-[11.5px] text-[hsl(var(--ec-text-2))]">
      {children}
    </span>
  );
}

export function Kicker({ children, mint = false, className }: { children: ReactNode; mint?: boolean; className?: string }) {
  return (
    <p
      className={cn("text-[11px] uppercase tracking-[0.14em]", mint ? "text-[hsl(var(--ec-mint))]" : "text-[hsl(var(--ec-muted))]", className)}
      style={{ fontFamily: MONO }}
    >
      {children}
    </p>
  );
}

export type GlyphName =
  | "arrow" | "back" | "mobile" | "desktop" | "export" | "plus"
  | "share" | "refresh" | "lock" | "alert" | "check" | "clock";

/**
 * The handful of glyphs the home and the report pages need, drawn here on a
 * 16px grid with square caps. Not an icon set: each one is used where a word
 * alone would be slower.
 */
export function Glyph({ name, size = 16, className }: { name: GlyphName; size?: number; className?: string }) {
  const paths: Record<GlyphName, ReactNode> = {
    arrow: (
      <>
        <path d="M3 8h10" />
        <path d="M9.5 4.5L13 8l-3.5 3.5" />
      </>
    ),
    mobile: (
      <>
        <rect x="5" y="2" width="6" height="12" />
        <path d="M7.4 11.8h1.2" />
      </>
    ),
    desktop: (
      <>
        <rect x="2" y="3" width="12" height="8" />
        <path d="M6 13.5h4" />
      </>
    ),
    export: (
      <>
        <path d="M8 2.5v7" />
        <path d="M5 6.5l3 3 3-3" />
        <path d="M3 11v2.5h10V11" />
      </>
    ),
    plus: (
      <>
        <path d="M8 3.5v9" />
        <path d="M3.5 8h9" />
      </>
    ),
    back: (
      <>
        <path d="M13 8H3" />
        <path d="M6.5 4.5L3 8l3.5 3.5" />
      </>
    ),
    share: (
      <>
        <path d="M9.5 2.5h4v4" />
        <path d="M13.5 2.5L7.5 8.5" />
        <path d="M11.5 9.5v4h-9v-9h4" />
      </>
    ),
    refresh: (
      <>
        <path d="M13 8a5 5 0 1 1-1.5-3.6" />
        <path d="M12 1.8v3h-3" />
      </>
    ),
    lock: (
      <>
        <rect x="3.5" y="7" width="9" height="6.5" />
        <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" />
      </>
    ),
    alert: (
      <>
        <circle cx="8" cy="8" r="6" />
        <path d="M8 4.8v3.8" />
        <path d="M8 10.8v.5" />
      </>
    ),
    check: <path d="M3.5 8.5l3 3 6-7" />,
    clock: (
      <>
        <circle cx="8" cy="8" r="6" />
        <path d="M8 4.8V8l2.2 1.6" />
      </>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="square" aria-hidden className={className}>
      {paths[name]}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Fit a fixed-size composition into whatever width is available       */
/* ------------------------------------------------------------------ */

/**
 * The annotated captures are compositions: a label, a line and a marker only
 * make sense at their drawn positions. So they are laid out once at a design
 * size and scaled down as a whole when the column is narrower, never reflowed.
 */
export function FitScale({ width, height, children, className }: { width: number; height: number; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [s, setS] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setS(Math.min(1, el.clientWidth / width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  return (
    <div ref={ref} className={cn("w-full min-w-0 max-w-full", className)}>
      <div style={{ width: width * s, height: height * s, margin: "0 auto" }}>
        <div style={{ width, height, transform: `scale(${s})`, transformOrigin: "0 0" }}>{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Capture with callouts                                               */
/* ------------------------------------------------------------------ */

export type Callout = { n: number; side: "left" | "right"; y: number; title: string; sub: string };

/**
 * A capture with its findings labelled beside it, each label joined to its
 * marker by a thin line that ends in the finding's number.
 */
export function Annotated({
  kind = "mobile",
  scale,
  capX,
  capY,
  width,
  height,
  markers,
  callouts,
  labelW = 172,
  label,
  large = false,
}: {
  kind?: "mobile" | "desktop";
  scale: number;
  capX: number;
  capY: number;
  width: number;
  height: number;
  markers: Marker[];
  callouts: Callout[];
  labelW?: number;
  label?: string;
  /** Bigger label text, for compositions drawn to be scaled down onto a phone. */
  large?: boolean;
}) {
  const baseW = kind === "mobile" ? MOBILE.w : DESKTOP.w;
  const cw = baseW * scale;
  const byN = new Map(markers.map((m) => [m.n, m]));
  const joins = callouts.map((c) => {
    const m = byN.get(c.n)!;
    const my = capY + (m.y + m.h / 2) * scale;
    const left = c.side === "left";
    const mx = left ? capX + m.x * scale : capX + (m.x + m.w) * scale;
    const lx = left ? capX - 26 - labelW : capX + cw + 26;
    const ex = left ? lx + labelW : lx;
    const elbow = left ? capX - 12 : capX + cw + 12;
    return { c, mx, my, lx, ex, ey: c.y + 17, elbow };
  });
  return (
    <div className="relative" style={{ width, height }}>
      <div className="absolute" style={{ left: capX, top: capY }}>
        <Capture kind={kind} scale={scale} markers={markers} showNumbers={callouts.length === 0} label={label} />
      </div>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="pointer-events-none absolute left-0 top-0" aria-hidden>
        {joins.map(({ c, mx, my, ex, ey, elbow }) => (
          <g key={c.n}>
            <path d={`M${ex} ${ey} H${elbow} L${mx} ${my}`} fill="none" stroke="hsl(var(--ec-mint))" strokeOpacity={0.7} strokeWidth={1} />
            <circle cx={ex} cy={ey} r={2} fill="hsl(var(--ec-mint))" fillOpacity={0.8} />
          </g>
        ))}
      </svg>
      {joins.map(({ c, mx, my }) => (
        <div key={`n${c.n}`} className="absolute" style={{ left: mx - 11, top: my - 11 }}>
          <Num n={c.n} onImage />
        </div>
      ))}
      {joins.map(({ c, lx }) => (
        <div
          key={`l${c.n}`}
          className="absolute flex items-start gap-2 rounded-[10px] border border-[hsl(var(--ec-line-strong))] bg-card/95 px-2.5 py-2"
          style={{ left: lx, top: c.y, width: labelW }}
        >
          <Num n={c.n} size={large ? 20 : 18} />
          <div className="min-w-0">
            <p className={cn("font-semibold leading-[1.3] text-foreground", large ? "text-[14.5px]" : "text-[12px]")}>{c.title}</p>
            <p className={cn("mt-0.5 leading-[1.35] text-[hsl(var(--ec-muted))]", large ? "text-[13px]" : "text-[11px]")}>{c.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The score dial                                                      */
/* ------------------------------------------------------------------ */

/*
 * Chart colours for the dial ticks: the low and high ends of the fill, mixed
 * per tick. They describe a ramp inside one chart, not a theme surface.
 */
const RAMP_LO = [26, 107, 77];
const RAMP_HI = [143, 240, 196];
const mix = (t: number) => `rgb(${RAMP_LO.map((a, i) => Math.round(a + (RAMP_HI[i] - a) * t)).join(",")})`;

/**
 * The score as an instrument: sixty ticks round a 270° arc. Ticks up to the
 * score are lit on a ramp, ticks up to what the top changes would reach are
 * drawn faintly, the rest are dark, and a hairline outside the ring marks the
 * category median. One glance gives where the page is, where it could be, and
 * how that compares.
 */
export function ScoreDial({
  size = 150,
  score,
  reach,
  median,
  ticks = 54,
  scale = false,
}: {
  size?: number;
  score: number;
  reach?: number;
  median?: number;
  ticks?: number;
  /** Draw the lit ticks on the red → orange → yellow → green score scale (analysis reports). */
  scale?: boolean;
}) {
  const c = size / 2;
  const accent = scale ? scoreColor(score) : "hsl(var(--ec-mint))";
  const rOut = size * 0.46;
  const rIn = size * 0.385;
  const start = 135;
  const sweep = 270;
  const pt = (r: number, v: number) => {
    const a = ((start + (sweep * v) / 100) * Math.PI) / 180;
    return [c + r * Math.cos(a), c + r * Math.sin(a)];
  };
  const rr = rIn - size * 0.05;
  const [x0, y0] = pt(rr, 0);
  const [x1, y1] = pt(rr, 100);
  const [xs, ys] = pt(rr, score);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="block overflow-visible">
      {Array.from({ length: ticks }, (_, i) => {
        const v = ((i + 0.5) * 100) / ticks;
        const a = ((start + (sweep * (i + 0.5)) / ticks) * Math.PI) / 180;
        const ri = rIn - (i % 5 === 0 ? size * 0.012 : 0);
        const lit = v <= score;
        const reachable = !lit && reach !== undefined && v <= reach;
        return (
          <line
            key={i}
            x1={c + ri * Math.cos(a)}
            y1={c + ri * Math.sin(a)}
            x2={c + rOut * Math.cos(a)}
            y2={c + rOut * Math.sin(a)}
            stroke={lit ? (scale ? scoreColor(v) : mix(v / score)) : reachable ? accent : "#ffffff"}
            strokeOpacity={lit ? 1 : reachable ? 0.32 : 0.08}
            strokeWidth={size * 0.011}
            strokeLinecap="round"
          />
        );
      })}
      <path d={`M${x0} ${y0} A${rr} ${rr} 0 1 1 ${x1} ${y1}`} fill="none" stroke="#ffffff" strokeOpacity={0.07} strokeWidth={1} />
      <path d={`M${x0} ${y0} A${rr} ${rr} 0 ${(sweep * score) / 100 > 180 ? 1 : 0} 1 ${xs} ${ys}`} fill="none" stroke={accent} strokeOpacity={0.55} strokeWidth={1.2} />
      <circle cx={xs} cy={ys} r={size * 0.012} fill={accent} />
      {median !== undefined &&
        (() => {
          const [ox, oy] = pt(rOut + size * 0.03, median);
          const [ix, iy] = pt(rOut + size * 0.005, median);
          return <line x1={ox} y1={oy} x2={ix} y2={iy} stroke="#ffffff" strokeOpacity={0.6} strokeWidth={1.2} />;
        })()}
    </svg>
  );
}

/** Dial plus its centred numeral, for the places the score is shown compact. */
export function ScoreBadge({ size = 150, score, reach, median, caption, scale }: { size?: number; score: number; reach?: number; median?: number; caption?: string; scale?: boolean }) {
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size * 0.88 }}>
      <ScoreDial size={size} score={score} reach={reach} median={median} scale={scale} />
      <p
        className="absolute inset-x-0 text-center font-medium leading-none text-foreground"
        style={{ top: size * 0.29, fontSize: size * 0.31, letterSpacing: "-0.05em", fontVariantNumeric: "tabular-nums" }}
      >
        {score}
      </p>
      {caption && (
        <p className="absolute inset-x-0 text-center text-[hsl(var(--ec-muted))]" style={{ top: size * 0.63, fontSize: Math.max(10, size * 0.07), fontFamily: MONO }}>
          {caption}
        </p>
      )}
    </div>
  );
}
